import { Section, Text } from '@ui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { errorMessage } from '../../../lib/errors';
import { useColonyFamily, type FamilyMember } from '../api';
import { formatOriginDate } from '../format';
import { StatusBadge } from './StatusBadge';
import styles from './FamilyTree.module.css';

/**
 * Family tree of a colony (VOL-009): from the oldest known ancestor down to
 * all its splits and swarms. Hidden while the colony has no relatives.
 */
export function FamilyTree({ colonyId }: { colonyId: string }) {
  const { t } = useTranslation();
  const family = useColonyFamily(colonyId);
  if (family.error) {
    return (
      <Section title={t('apiary.colony.family')} variant="content">
        <Text tone="danger" role="alert">
          {errorMessage(family.error)}
        </Text>
      </Section>
    );
  }
  const members = family.data ?? [];
  if (members.length < 2) return null;

  const children = new Map<string | null, FamilyMember[]>();
  for (const m of members) {
    const key = m.parent_id ?? null;
    children.set(key, [...(children.get(key) ?? []), m]);
  }

  const branch = (parent: string | null, root = false) => {
    const list = children.get(parent);
    if (!list) return null;
    return (
      <ul className={root ? styles.tree : styles.children}>
        {list.map((m) => (
          <li key={m.id} className={styles.item}>
            <span className={styles.node}>
              {m.id === colonyId ? (
                <span className={styles.current} aria-current="true">
                  {m.name}
                </span>
              ) : (
                <Link
                  to={`/voelker/${m.id}`}
                  className={m.status === 'active' ? undefined : styles.gone}
                >
                  {m.name}
                </Link>
              )}
              <span className={styles.meta}>
                {[
                  m.origin && t(`apiary.origin.${m.origin}`),
                  formatOriginDate(m.origin_date, m.origin_precision),
                ]
                  .filter(Boolean)
                  .join(' · ')}
                {m.status !== 'active' && (
                  <>
                    {' '}
                    <StatusBadge status={m.status} />
                  </>
                )}
              </span>
            </span>
            {branch(m.id)}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Section title={t('apiary.colony.family')} variant="content">
      {branch(null, true)}
    </Section>
  );
}
